import React, { useState } from 'react';
import SEO from '../components/SEO';
import toast from 'react-hot-toast';
import { saveVolunteerApplication } from '../config/firebase';

const VolunteerPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    professionType: '', // 'medical' or 'non-medical'
    profession: '',
    location: '',
    howToHelp: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await toast.promise(
        saveVolunteerApplication(formData),
        {
          loading: 'Submitting your application...', 
          success: () => {
            setFormData({
              name: '',
              email: '',
              phone: '',
              professionType: '',
              profession: '',
              location: '',
              howToHelp: '',
            });
            return 'Thank you for your interest in volunteering! We will get back to you soon.';
          },
          error: 'Failed to submit application. Please try again.',
        }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Volunteer with Healthier Smile Initiatives"
        description="Join Healthier Smile Initiatives as a volunteer. Apply to lend your skills and time to support our mission in Sierra Leone."
      />
      <main className="bg-stone-50 py-16 md:py-24">
        <div className="container mx-auto px-6 max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-bold text-center text-teal-700 mb-8">Volunteer With Us</h1>
          <p className="text-lg text-stone-600 text-center mb-12">Your time and skills can make a significant difference. Fill out the form below to join our team of dedicated volunteers.</p>

          <div className="bg-white p-8 md:p-12 rounded-2xl shadow-lg border border-stone-200">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-stone-700">Full Name</label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="mt-1 block w-full px-4 py-2 border border-stone-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-stone-700">Email Address</label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="mt-1 block w-full px-4 py-2 border border-stone-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-stone-700">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  id="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={loading}
                  className="mt-1 block w-full px-4 py-2 border border-stone-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="professionType" className="block text-sm font-medium text-stone-700">Type of Profession</label>
                <select
                  name="professionType"
                  id="professionType"
                  value={formData.professionType}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="mt-1 block w-full px-4 py-2 border border-stone-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                >
                  <option value="">Select...</option>
                  <option value="medical">Medical Professional</option>
                  <option value="non-medical">Non-Medical Professional</option>
                </select>
              </div>

              {formData.professionType && (
                <div>
                  <label htmlFor="profession" className="block text-sm font-medium text-stone-700">Your Profession / Field</label>
                  <input
                    type="text"
                    name="profession"
                    id="profession"
                    value={formData.profession}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="mt-1 block w-full px-4 py-2 border border-stone-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                  />
                </div>
              )}

              <div>
                <label htmlFor="location" className="block text-sm font-medium text-stone-700">Your Location (City, Country)</label>
                <input
                  type="text"
                  name="location"
                  id="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="mt-1 block w-full px-4 py-2 border border-stone-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="howToHelp" className="block text-sm font-medium text-stone-700">How would you like to help? (e.g., clinical work, administration, education, fundraising)</label>
                <textarea
                  name="howToHelp"
                  id="howToHelp"
                  rows={4}
                  value={formData.howToHelp}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="mt-1 block w-full px-4 py-2 border border-stone-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white ${loading ? 'bg-teal-400' : 'bg-teal-600 hover:bg-teal-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500`}
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        </div>
      </main>
    </>
  );
};

export default VolunteerPage;